import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PubSub } from "@aws-amplify/pubsub";
import { post } from "aws-amplify/api";
import { fetchAuthSession, fetchUserAttributes } from "aws-amplify/auth";

type Status = "idle" | "loading" | "success" | "error";

const DEFAULT_API_NAME =
  process.env.REACT_APP_ATTACH_IOT_POLICY_API_NAME || "attachIoTPolicyAPI";
const DEFAULT_PATH =
  process.env.REACT_APP_ATTACH_IOT_POLICY_API_PATH || "/attach";
const DEFAULT_IOT_ENDPOINT =
  process.env.REACT_APP_IOT_ENDPOINT ||
  "wss://aa548ngrivwoo-ats.iot.eu-west-1.amazonaws.com/mqtt";
const DEFAULT_IOT_REGION =
  process.env.REACT_APP_IOT_REGION || "eu-west-1";

const IoTPolicyTest: React.FC = () => {
  const [identityId, setIdentityId] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState<string>("");
  const [apiName, setApiName] = useState(DEFAULT_API_NAME);
  const [path, setPath] = useState(DEFAULT_PATH);
  const [responseJson, setResponseJson] = useState<any>(null);
  const [topic, setTopic] = useState("accounts/+/notifications/global");
  const [events, setEvents] = useState<
    { ts: string; topic: string; payload: any }[]
  >([]);
  const [subscription, setSubscription] = useState<{
    unsubscribe: () => void;
  } | null>(null);
  const [iotEndpoint, setIotEndpoint] = useState(DEFAULT_IOT_ENDPOINT);
  const [iotRegion, setIotRegion] = useState(DEFAULT_IOT_REGION);

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const attributes = await fetchUserAttributes();
        
        setIdentityId(session.identityId);
        setUserId(attributes.sub);
      } catch (err) {
        console.error("Failed to load user info", err);
        setMessage("Erro ao obter identityId/userId (login requerido?).");
      }
    })();
  }, []);

  const canSubmit = useMemo(
    () => Boolean(apiName) && Boolean(path) && Boolean(identityId) && Boolean(userId),
    [apiName, path, identityId, userId]
  );

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      subscription?.unsubscribe();
    };
  }, [subscription]);

  // Create PubSub instance with current config
  const pubSubRef = useRef<PubSub | null>(null);
  
  const getOrCreatePubSub = useCallback(() => {
    // Always create a new instance when endpoint/region changes
    if (
      !pubSubRef.current ||
      (pubSubRef.current as any)._config?.endpoint !== iotEndpoint ||
      (pubSubRef.current as any)._config?.region !== iotRegion
    ) {
      pubSubRef.current = new PubSub({
        region: iotRegion,
        endpoint: iotEndpoint,
      });
    }
    return pubSubRef.current;
  }, [iotEndpoint, iotRegion]);

  const handleAttach = async () => {
    setStatus("loading");
    setMessage("");
    setResponseJson(null);

    try {
      const response = await post({
        apiName,
        path,
        options: {
          body: { 
            userId: userId!,
            identityId: identityId!
          },
        },
      }).response;

      const data = await response.body.json() as any;
      setStatus("success");
      setResponseJson(data);
      setMessage(JSON.stringify(data, null, 2));
    } catch (err: any) {
      console.error("AttachIoTPolicy call failed:", err);
      setStatus("error");
      setMessage(err?.message || "Erro ao chamar attachIoTPolicy");
    }
  };

  const handleSubscribe = () => {
    subscription?.unsubscribe();
    setEvents([]);

    if (!topic) {
      setMessage("Informe um tópico para inscrever");
      return;
    }

    try {
      const pubsub = getOrCreatePubSub();
      const sub = pubsub.subscribe({ topics: topic }).subscribe({
        next: (data) => {
          const payload = data;
          const ts = new Date().toISOString();
          setEvents((prev) => [{ ts, topic, payload }, ...prev].slice(0, 50));
        },
        error: (err: Error) => {
          console.error("Subscription error", err);
          setMessage(err?.message || "Erro ao receber eventos");
        },
        complete: () => {
          setMessage("Subscription encerrada");
          setSubscription(null);
        },
      });
      setSubscription(sub);
      setMessage(`Subscribed to ${topic}`);
    } catch (err: any) {
      console.error("Failed to subscribe", err);
      setMessage(err?.message || "Erro ao iniciar subscription");
    }
  };

  const handleUnsubscribe = () => {
    subscription?.unsubscribe();
    setSubscription(null);
    setMessage("Subscription cancelada");
  };

  return (
    <div style={{ padding: "24px", maxWidth: 720 }}>
      <h2>Teste Attach IoT Policy (Multi-tenant)</h2>
      <p>
        Usa API Gateway para anexar a policy IoT ao
        Cognito Identity atual, com acesso às accounts do usuário.
      </p>

      <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span>API name</span>
          <input
            value={apiName}
            onChange={(e) => setApiName(e.target.value)}
            placeholder="attachIoTPolicyAPI"
            style={{ padding: 8 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span>Path</span>
          <input
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="/attach"
            style={{ padding: 8 }}
          />
        </label>
      </div>

      <div style={{ marginBottom: "16px" }}>
        <p>
          <strong>UserId (sub):</strong>{" "}
          {userId || "Carregando ou indisponível (faça login)."}
        </p>
        <p>
          <strong>IdentityId:</strong>{" "}
          {identityId || "Carregando ou indisponível (faça login)."}
        </p>
      </div>

      <button
        onClick={handleAttach}
        disabled={status === "loading" || !canSubmit}
        style={{ padding: "8px 16px", marginTop: "8px", cursor: canSubmit ? "pointer" : "not-allowed" }}
      >
        {status === "loading" ? "Anexando..." : "Anexar IoT Policy"}
      </button>

      {status !== "idle" && (
        <div style={{ marginTop: "12px" }}>
          <strong>Status:</strong> {status}
          <div style={{ fontSize: 12, marginTop: 4, color: "#555" }}>
            Chamando <code>{apiName}{path}</code> com userId/identityId detectados.
          </div>
          <pre
            style={{
              background: "#f5f5f5",
              padding: "12px",
              borderRadius: "4px",
              whiteSpace: "pre-wrap",
            }}
          >
            {message}
          </pre>
          {responseJson && (
            <div style={{ marginTop: 8 }}>
              <div><strong>Policy Name:</strong> {responseJson.policyName}</div>
              <div><strong>Accounts:</strong> {Array.isArray(responseJson.accounts) ? responseJson.accounts.join(", ") : "-"}</div>
            </div>
          )}
        </div>
      )}

      <hr style={{ margin: "24px 0" }} />

      <h3>Testar subscription</h3>
      <p>Assina o tópico informado e mostra os eventos recebidos.</p>

      <div style={{ display: "grid", gap: 12, marginBottom: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span>IoT endpoint (wss)</span>
          <input
            value={iotEndpoint}
            onChange={(e) => setIotEndpoint(e.target.value)}
            placeholder="wss://xxxx-ats.iot.eu-west-1.amazonaws.com/mqtt"
            style={{ padding: 8 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span>Region</span>
          <input
            value={iotRegion}
            onChange={(e) => setIotRegion(e.target.value)}
            placeholder="eu-west-1"
            style={{ padding: 8 }}
          />
        </label>
      </div>

      <div style={{ display: "grid", gap: 12, marginBottom: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span>Tópico</span>
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="accounts/{accountId}/#"
            style={{ padding: 8 }}
          />
        </label>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={handleSubscribe} style={{ padding: "8px 12px" }}>
            {subscription ? "Reassinar" : "Assinar"}
          </button>
          <button
            onClick={handleUnsubscribe}
            disabled={!subscription}
            style={{ padding: "8px 12px", cursor: subscription ? "pointer" : "not-allowed" }}
          >
            Cancelar
          </button>
        </div>
      </div>

      <div>
        <strong>Eventos ({events.length})</strong>
        <div
          style={{
            marginTop: 8,
            background: "#f5f5f5",
            padding: 12,
            borderRadius: 4,
            maxHeight: 260,
            overflow: "auto",
          }}
        >
          {events.length === 0 && <div>Nenhum evento recebido ainda.</div>}
          {events.map((evt, idx) => (
            <div key={`${evt.ts}-${idx}`} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: "#666" }}>{evt.ts}</div>
              <div style={{ fontSize: 12, color: "#666" }}>Topic: {evt.topic}</div>
              <pre
                style={{
                  background: "#fff",
                  padding: 8,
                  borderRadius: 4,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  border: "1px solid #ddd",
                }}
              >
                {JSON.stringify(evt.payload, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IoTPolicyTest;
